import React from 'react';
import { Typography, Flex, Form, Input, Select, Button } from 'antd';

import { ModalTemplate } from './ModalTemplate';
import useTrackTrialEvent from '../useTrackTrialEvent';
import { useTrial } from '../useTrial';
import { CHURN_REASONS } from '../FreeTrial';

const OPTION_VALUES = {
  ...CHURN_REASONS,
  other: 'other',
} as const;

type OptionValue = typeof OPTION_VALUES[keyof typeof OPTION_VALUES];

const OPTIONS = [
  { label: 'Too expensive', value: OPTION_VALUES.too_expensive },
  { label: 'Not enough time to see benefits', value: OPTION_VALUES.not_enough_time },
  { label: 'Not enough features', value: OPTION_VALUES.not_enough_features },
  { label: 'Other', value: OPTION_VALUES.other },
];

function FeedbackChurnModal() {
  const [selectedOption, setSelectedOption] = React.useState<OptionValue>();
  const trackTrialEvent = useTrackTrialEvent();
  const { updateFreeTrialState, freeTrialState } = useTrial();
  const formId = React.useId();

  const open = freeTrialState.user.displayChurnModal;

  const onFinish = (values: { reason: OptionValue; feedback?: string }) => {
    trackTrialEvent('FeedbackChurn', { reason: values.reason, user_input: values.feedback || '' });

    if (values.reason !== OPTION_VALUES.other) {
      updateFreeTrialState({ state: { displayChurnModal: false, churnFollowupModal: values.reason }, scope: 'user' });
    } else {
      updateFreeTrialState({ state: { displayChurnModal: false }, scope: 'user' });
      updateFreeTrialState({ state: { unconvertedFlowEnded: true }, scope: 'company' });
    }
  };

  React.useEffect(() => {
    if (open) {
      trackTrialEvent('BannerShown', { banner_name: 'Survey', is_converted: false });
    }
  }, [open]);

  return (
    <ModalTemplate
      open={open}
      title="You chose not to upgrade—and that's completely fine."
      footer={[
        <Button key="submit" type="primary" htmlType="submit" form={formId}>
          Share Feedback
        </Button>,
      ]}
      closable={false}
    >
      <Typography.Paragraph>
        But note that rendering will stop. Crawlers won’t see your fully rendered pages.
      </Typography.Paragraph>
      <Typography.Paragraph>
        <Typography.Text strong>Could you share why you chose not to upgrade?</Typography.Text> Your feedback helps us
        improve. Thank you!
      </Typography.Paragraph>
      <Form onFinish={onFinish} name={formId}>
        <Flex gap="middle" vertical>
          <Form.Item
            required
            name="reason"
            style={{ margin: 0 }}
            rules={[{ required: true, message: 'Please select a reason' }]}
          >
            <Select
              placeholder="Select a reason"
              options={OPTIONS}
              onChange={(option) => setSelectedOption(option)}
            ></Select>
          </Form.Item>

          {selectedOption === OPTION_VALUES.other && (
            <Form.Item name="feedback" rules={[{ required: true, message: 'Please provide your feedback' }]}>
              <Input.TextArea
                placeholder="Your feedback is important to us. Please share your thoughts."
                autoSize={{ minRows: 5 }}
                maxLength={255}
                showCount
              />
            </Form.Item>
          )}
        </Flex>
      </Form>
    </ModalTemplate>
  );
}

export default FeedbackChurnModal;
